import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { reverseGeocodeCity, type ResolvedLocation } from "@/lib/geo";

const STORAGE_KEY = "marketlink_buyer_location";

export function useBuyerLocation(fallbackZip?: string | null) {
  const [buyerLocation, setBuyerLocation] = useState<ResolvedLocation | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ResolvedLocation) : null;
    } catch {
      return null;
    }
  });
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!buyerLocation) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(buyerLocation));
  }, [buyerLocation]);

  const handleUseCurrentLocation = useCallback(async () => {
    if (typeof window === "undefined") return;
    setIsLocating(true);
    setLocationError(null);

    const onSuccess = (latitude: number, longitude: number) => {
      setBuyerLocation({
        source: "browser_gps",
        label: null,
        zipCode: fallbackZip?.trim() || null,
        point: { latitude, longitude },
      });
      setIsLocating(false);
      reverseGeocodeCity(latitude, longitude).then((city) => {
        if (!city) return;
        setBuyerLocation((prev) =>
          prev && prev.source === "browser_gps" ? { ...prev, label: city } : prev,
        );
      });
    };

    const onError = (message: string) => {
      setLocationError(message);
      setIsLocating(false);
    };

    if (Capacitor.isNativePlatform()) {
      try {
        const { Geolocation } = await import("@capacitor/geolocation");
        const perm = await Geolocation.requestPermissions();
        if (perm.location === "denied") {
          onError("Location permission was denied. Check your device settings.");
          return;
        }
        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
        });
        onSuccess(pos.coords.latitude, pos.coords.longitude);
      } catch (e: any) {
        onError(e?.message || "Could not get current location.");
      }
      return;
    }

    if (!("geolocation" in navigator)) {
      onError("Location is not supported on this device/browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => onSuccess(position.coords.latitude, position.coords.longitude),
      (error) => {
        const message =
          error.code === error.PERMISSION_DENIED
            ? "Location permission was denied."
            : error.code === error.POSITION_UNAVAILABLE
              ? "Current location is unavailable."
              : error.code === error.TIMEOUT
                ? "Location request timed out."
                : "Could not get current location.";
        onError(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  }, [fallbackZip]);

  return {
    buyerLocation,
    setBuyerLocation,
    isLocating,
    locationError,
    handleUseCurrentLocation,
  };
}
